import { Fragment, useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Users, LayoutGrid, GraduationCap, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const CLASSES = ["Play Group", "LKG", "UKG", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10"];
const SECTION_LETTERS = ["A", "B", "C", "D", "E", "F"];
const NONE = "none";

const classLabel = (c) => {
  if (c === "LKG" || c === "UKG" || c === "Play Group") return c;
  const n = parseInt(c, 10);
  const s = ["th", "st", "nd", "rd"], v = n % 100;
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
};

export default function AdminSections({ embedded } = {}) {
  const { user } = useAuth();
  const isReadOnly = user?.role === "super_admin";
  const [sections, setSections] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ class_name: "5", section: "C", class_teacher_id: NONE });
  const [delTarget, setDelTarget] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [s, t] = await Promise.all([api.get("/admin/sections"), api.get("/admin/teachers")]);
      setSections(s.data.sections || []);
      setTeachers((t.data || []).filter((x) => x.role === "teacher"));
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Failed to load sections");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const changeTeacher = async (sec, teacherId) => {
    try {
      await api.put(`/admin/sections/${sec.id}`, { class_teacher_id: teacherId === NONE ? null : teacherId });
      toast.success("Class teacher updated");
      load();
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Failed to update class teacher");
    }
  };

  const createSection = async () => {
    setSaving(true);
    try {
      await api.post("/admin/sections", {
        class_name: form.class_name,
        section: form.section,
        class_teacher_id: form.class_teacher_id === NONE ? null : form.class_teacher_id,
      });
      toast.success(`Section ${classLabel(form.class_name)} · ${form.section} created`);
      setOpen(false);
      setForm({ class_name: "5", section: "C", class_teacher_id: NONE });
      load();
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Failed to create section");
    } finally { setSaving(false); }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/admin/sections/${delTarget.id}`);
      toast.success("Section deleted");
      setDelTarget(null);
      load();
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Failed to delete section");
      setDelTarget(null);
    }
  };

  const unassignedCount = sections.filter((s) => !s.class_teacher).length;

  const Wrap = embedded ? "div" : Layout;
  return (
    <Wrap>
      <div className="space-y-6" data-testid="admin-sections-page">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 text-sky-600 text-sm font-medium"><LayoutGrid className="h-4 w-4" /> Sections</div>
            <h1 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100 mt-1" style={{ fontFamily: "Outfit" }}>Manage Sections</h1>
            <p className="text-slate-500 dark:text-slate-400 mt-1 text-sm">Each section has one class teacher — only they can take that section's attendance.</p>
          </div>
          {!isReadOnly && (
            <Button onClick={() => setOpen(true)} className="h-11 rounded-xl bg-sky-500 hover:bg-sky-600" data-testid="add-section-btn">
              <Plus className="h-4 w-4 mr-1" /> Add section
            </Button>
          )}
        </div>

        {unassignedCount > 0 && (
          <div className="flex items-center gap-2 text-amber-800 bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5 text-sm" data-testid="unassigned-banner">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            {unassignedCount} section{unassignedCount === 1 ? "" : "s"} have no class teacher assigned yet.
          </div>
        )}

        <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl overflow-hidden">
          <div className="grid grid-cols-12 gap-3 px-5 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wide border-b border-slate-100 dark:border-slate-700">
            <div className="col-span-3">Class · Section</div>
            <div className="col-span-5">Class teacher</div>
            <div className="col-span-2">Students</div>
            <div className="col-span-2 text-right">Actions</div>
          </div>
          {loading ? (
            <div className="px-5 py-10 text-center text-slate-400 text-sm">Loading sections…</div>
          ) : sections.length === 0 ? (
            <div className="px-5 py-10 text-center text-slate-400 text-sm">No sections yet.</div>
          ) : sections.map((s) => (
            <div key={s.id} className="grid grid-cols-12 gap-3 px-5 py-3 items-center border-b border-slate-50 dark:border-slate-700/50 hover:bg-slate-50/60 dark:hover:bg-slate-700/30" data-testid={`section-row-${s.class_name}-${s.section}`}>
              <div className="col-span-3 font-medium text-slate-800 dark:text-slate-100">
                {classLabel(s.class_name)} <span className="text-slate-400">·</span> Section {s.section}
              </div>
              <div className="col-span-5">
                <Select value={s.class_teacher?.id || NONE} onValueChange={(v) => changeTeacher(s, v)} disabled={isReadOnly}>
                  <SelectTrigger className="h-9 rounded-lg max-w-xs" data-testid={`ct-select-${s.class_name}-${s.section}`}>
                    <SelectValue placeholder="Assign class teacher" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>— No class teacher —</SelectItem>
                    {teachers.map((t) => (
                      <SelectItem key={t.id} value={t.id}>{t.name}{t.subject ? ` (${t.subject})` : ""}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2 flex items-center gap-1.5 text-slate-600 dark:text-slate-300 text-sm">
                <Users className="h-3.5 w-3.5 text-slate-400" /> {s.student_count}
              </div>
              <div className="col-span-2 text-right">
                {!isReadOnly && (
                  <Button variant="ghost" size="icon" className="h-9 w-9 text-rose-500 hover:text-rose-600 hover:bg-rose-50"
                    onClick={() => setDelTarget(s)} data-testid={`delete-section-${s.class_name}-${s.section}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Add section dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add section</DialogTitle>
            <DialogDescription>Create a new class-section and optionally assign its class teacher.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Class</Label>
                <Select value={form.class_name} onValueChange={(v) => setForm((f) => ({ ...f, class_name: v }))}>
                  <SelectTrigger className="h-10 rounded-xl mt-1.5" data-testid="new-section-class"><SelectValue /></SelectTrigger>
                  <SelectContent>{CLASSES.map((c) => <SelectItem key={c} value={c}>{classLabel(c)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Section</Label>
                <Select value={form.section} onValueChange={(v) => setForm((f) => ({ ...f, section: v }))}>
                  <SelectTrigger className="h-10 rounded-xl mt-1.5" data-testid="new-section-letter"><SelectValue /></SelectTrigger>
                  <SelectContent>{SECTION_LETTERS.map((s) => <SelectItem key={s} value={s}>Section {s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="flex items-center gap-1.5"><GraduationCap className="h-3.5 w-3.5" /> Class teacher</Label>
              <Select value={form.class_teacher_id} onValueChange={(v) => setForm((f) => ({ ...f, class_teacher_id: v }))}>
                <SelectTrigger className="h-10 rounded-xl mt-1.5" data-testid="new-section-teacher"><SelectValue placeholder="Optional" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>— Assign later —</SelectItem>
                  {teachers.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}{t.subject ? ` (${t.subject})` : ""}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={createSection} disabled={saving} className="bg-sky-500 hover:bg-sky-600" data-testid="save-section-btn">
              {saving ? "Saving…" : "Create section"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete section?</AlertDialogTitle>
            <AlertDialogDescription>
              {delTarget && `This will remove ${classLabel(delTarget.class_name)} · Section ${delTarget.section}. Sections with students cannot be deleted.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-rose-500 hover:bg-rose-600" data-testid="confirm-delete-section">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Wrap>
  );
}
